import { useEffect, useMemo, useState } from "react";
import { DialogProps } from "../../types/dialog-types";
import { getTigerResource } from "../../utils/tigerResource";
import { formatTigerDateTime } from "../../utils/tigerTime";
import { acrylicStyles, acrylicDialogSurface } from "../../theme/acrylic";

/** One latched diagnostic filter, as returned by the `request_diag_filters`
 *  command. Mirrors the Rust `DiagFilterRecord`. */
interface DiagFilterRecord {
  config_name: string;
  msg_id: string;
  msg_str: string;
  turbine: string;
  time: number;
}

type SortKey = "msg_id" | "msg_str" | "turbine" | "time";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Stable identity for a record (config + token + time), used to drop the row
 *  locally after a successful remove. */
function sameRecord(a: DiagFilterRecord, b: DiagFilterRecord): boolean {
  return a.config_name === b.config_name && a.msg_id === b.msg_id && a.time === b.time;
}

// Shared Acrylic Glass theme (see src/theme/acrylic.ts): a frosted overlay over
// a rounded, resizable card with a header (title + ✕), a scrollable body, and a
// footer carrying the primary/secondary actions.
const styles = {
  ...acrylicStyles,
  // Offsets centre a 720 × 540 card.
  dialog: acrylicDialogSurface({ width: 720, height: 540, minWidth: 480, minHeight: 360 }),
  // Bespoke layout: the single section/table fills the body height.
  section: {
    ...acrylicStyles.section,
    flex: 1,
    minHeight: 0,
  },
  tableWrap: {
    ...acrylicStyles.tableWrap,
    flex: 1,
    minHeight: 0,
    display: "flex",
  },
  errorText: {
    color: "#b00020",
    fontSize: 12,
  },
  progressText: {
    fontStyle: "italic",
    opacity: 0.75,
    padding: "16px 8px",
    textAlign: "center",
  },
} as const;

/**
 * Diagnostics Filter manager — lists the currently latched/filtered diagnostic
 * messages and lets the user remove them. Port of the C++ `diag_filter_shell`:
 * on open it requests the filter list (DIAG_FILTER_INFO); the four columns are
 * sortable by header click (toggling direction on repeat); Remove sends
 * DIAG_FILTER_REMOVE for the selected row and drops it from the list. Styled to
 * match the Graph Channel Selection dialog.
 *
 * `config`: in the live/replay interface the dialog is scoped to the
 * currently-selected turbine — only that config's filters are listed. The
 * alarm interface passes "" (or omits it) and sees every turbine, matching the
 * C++ dialog.
 */
export function DiagFilterDialog({ open, onClose, config }: DialogProps & { config?: string }) {
  const [records, setRecords] = useState<DiagFilterRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [sortKey, setSortKey] = useState<SortKey>("msg_id");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [error, setError] = useState("");

  // Fetch the filter list each time the dialog opens — the C++ sends a fresh
  // DIAG_FILTER_INFO request in Ui_display_diagnostics_filter_dialog. Until the
  // DIAG_FILTER_INFO reply lands, `loading` shows a progress message in place
  // of the (stale) rows.
  useEffect(() => {
    if (!open) return;
    setSelectedIndex(-1);
    setError("");
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        let recs: DiagFilterRecord[];
        if (isTauri()) {
          const { invoke } = await import("@tauri-apps/api/core");
          recs = await invoke<DiagFilterRecord[]>("request_diag_filters");
        } else {
          const res = await fetch("/api/request_diag_filters");
          if (!res.ok) throw new Error(await res.text());
          recs = (await res.json()) as DiagFilterRecord[];
        }
        if (!cancelled) setRecords(recs);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleHeaderClick = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setSelectedIndex(-1);
  };

  // Turbine scoping (live/replay interface): only the selected config's rows.
  const visible = useMemo(
    () => (config ? records.filter((r) => r.config_name === config) : records),
    [records, config],
  );

  // Sort by the active column. Ties fall back to (msg_id, turbine, time), the
  // C++ `diag_filter_info_cls::comp` secondary ordering.
  const sorted = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    const tie = (a: DiagFilterRecord, b: DiagFilterRecord) =>
      a.msg_id.localeCompare(b.msg_id) ||
      a.turbine.localeCompare(b.turbine) ||
      a.time - b.time;
    const cmp = (a: DiagFilterRecord, b: DiagFilterRecord) => {
      let r: number;
      switch (sortKey) {
        case "time":
          r = a.time - b.time || tie(a, b);
          break;
        case "msg_str":
          r = a.msg_str.localeCompare(b.msg_str) || tie(a, b);
          break;
        case "turbine":
          r = a.turbine.localeCompare(b.turbine) || tie(a, b);
          break;
        case "msg_id":
        default:
          r = tie(a, b);
          break;
      }
      return r * dir;
    };
    return [...visible].sort(cmp);
  }, [visible, sortKey, sortDir]);

  const handleRemove = async () => {
    if (selectedIndex < 0 || selectedIndex >= sorted.length) return;
    const rec = sorted[selectedIndex];
    try {
      if (isTauri()) {
        const { invoke } = await import("@tauri-apps/api/core");
        await invoke("remove_diag_filter", {
          configName: rec.config_name,
          tokenId: rec.msg_id,
        });
      } else {
        const res = await fetch("/api/remove_diag_filter", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ config_name: rec.config_name, token_id: rec.msg_id }),
        });
        if (!res.ok) throw new Error(await res.text());
      }
    } catch (e) {
      setError(String(e));
      return;
    }
    setRecords((prev) => prev.filter((r) => !sameRecord(r, rec)));
    setSelectedIndex(-1);
  };

  const columns: { key: SortKey; label: string; width?: number }[] = [
    { key: "msg_id", label: getTigerResource("TIGER_DIAG_FILTER_MESID"), width: 110 },
    { key: "msg_str", label: getTigerResource("TIGER_DIAG_FILTER_MES"), width: 320 },
    { key: "turbine", label: getTigerResource("TIGER_DIAG_FILTER_TURBINE"), width: 140 },
    { key: "time", label: getTigerResource("TIGER_DIAG_FILTER_DATE"), width: 150 },
  ];

  if (!open) return null;

  const removeDisabled = selectedIndex < 0;

  return (
    <div style={styles.overlay}>
      <div
        style={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={getTigerResource("TIGER_DIAG_FILTER_TITLE")}
      >
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>{getTigerResource("TIGER_DIAG_FILTER_TITLE")}</span>
          <button className="acrylic-close-btn" onClick={onClose}>
            &#x2715;
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          <div style={styles.section}>
            <span style={styles.sectionLabel}>{getTigerResource("TIGER_DIAG_FILTER")}</span>
            <div style={styles.tableWrap}>
              <div className="list-control" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      {columns.map((col) => {
                        const isSorted = sortKey === col.key;
                        const indicator = isSorted ? (sortDir === "desc" ? " ▼" : " ▲") : "";
                        return (
                          <th
                            key={col.key}
                            style={{
                              ...(col.width ? { width: col.width } : {}),
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={() => handleHeaderClick(col.key)}
                          >
                            {col.label}
                            {indicator}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={columns.length} style={styles.progressText}>
                          {getTigerResource("TIGER_DIAG_IN_PROGRESS")}…
                        </td>
                      </tr>
                    ) : (
                      sorted.map((r, idx) => (
                        <tr
                          key={idx}
                          className={selectedIndex === idx ? "selected" : ""}
                          onClick={() => setSelectedIndex(idx)}
                        >
                          <td>{r.msg_id}</td>
                          <td>{r.msg_str}</td>
                          <td>{r.turbine}</td>
                          <td>{formatTigerDateTime(r.time)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {error && <div style={styles.errorText}>{error}</div>}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            className="btn-acrylic-primary"
            onClick={handleRemove}
            disabled={removeDisabled}
          >
            {getTigerResource("TIGER_RES_REMOVE")}
          </button>
          <button className="btn-acrylic-secondary" onClick={onClose}>
            {getTigerResource("TIGER_RES_CLOSE")}
          </button>
        </div>
      </div>
    </div>
  );
}
